import countriesConfig from '@/configs/countries/supported.json';

/**
 * Supported-country registry (config, not code).
 *
 * The JSON file is the single source of truth for dial codes, the phone-input
 * picker, and the `customers.country` attribute. Read it here, never inline a
 * dial code or country list in a component or server helper — `RULES.md` §15.
 *
 * This module is imported from both the server (`lib/server/*`) and the client
 * (`components/ui/phone-input.tsx`), so it must stay free of `server-only` and
 * of any non-JSON import.
 */
export interface SupportedCountry {
  readonly iso2: string;
  readonly iso3: string;
  readonly dial: string;
  readonly name: string;
  readonly flag: string;
  readonly currency: string;
  readonly momo: boolean;
  readonly paystack: boolean;
  /** IANA timezones whose wall clock identifies this market (device detection). */
  readonly timezones?: readonly string[];
}

interface CountriesConfig {
  countries: SupportedCountry[];
}

const config = countriesConfig as unknown as CountriesConfig;

/** Every supported country, in picker order (Ghana first). */
export const SUPPORTED_COUNTRIES: readonly SupportedCountry[] = config.countries;

/** The platform fallback when no country is supplied or the supplied one is unknown. */
export const DEFAULT_COUNTRY_ISO2 = 'GH';

/** Look up a country by ISO 3166-1 alpha-2 code (case-insensitive). */
export function getCountryByIso2(
  iso2: string | null | undefined,
  table: readonly SupportedCountry[] = SUPPORTED_COUNTRIES
): SupportedCountry | undefined {
  if (!iso2) return undefined;
  const code = iso2.trim().toUpperCase();
  return table.find((country) => country.iso2 === code);
}

/** True when the ISO2 code is a supported country. */
export function isSupportedCountry(iso2: string | null | undefined): boolean {
  return getCountryByIso2(iso2) !== undefined;
}

/**
 * Best-effort default country from a BCP-47 locale/language tag (`en-NG` -> NG).
 * Region subtags are the only input — never geolocation. Unsupported or
 * regionless locales fall back to the platform default.
 */
export function detectCountryFromLocale(locale: string | null | undefined): string {
  if (typeof locale !== 'string' || !locale) return DEFAULT_COUNTRY_ISO2;
  const region = locale.split(/[-_]/)[1];
  return isSupportedCountry(region) ? region.toUpperCase() : DEFAULT_COUNTRY_ISO2;
}

/**
 * Resolve a supported ISO2 code from an IANA timezone (e.g. `Africa/Accra` -> GH).
 * Returns null when the zone does not belong to a supported market.
 *
 * The device's timezone is a far better signal of the market a phone number
 * belongs to than the UI language: a Ghanaian on an English (US/GB) system
 * locale is still in Ghana, and the old locale-only detection defaulted them to
 * the United States — the wrong dial code, so their SMS code went nowhere. The
 * clock is not geolocation (it travels with the account, not the GPS), which
 * keeps this within the "never ask for geolocation" rule.
 */
export function detectCountryFromTimezone(timeZone: string | null | undefined): string | null {
  if (typeof timeZone !== 'string' || !timeZone) return null;
  for (const country of SUPPORTED_COUNTRIES) {
    if (country.timezones?.includes(timeZone)) return country.iso2;
  }
  return null;
}

/**
 * Best-effort market for the current device: the device timezone first (where
 * the number will actually be dialled), then the UI language, then the platform
 * default (Ghana). Never geolocation. Safe to call only in a browser; server
 * callers get the platform default.
 */
export function detectDeviceCountry(): string {
  if (typeof navigator === 'undefined') return DEFAULT_COUNTRY_ISO2;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fromTz = detectCountryFromTimezone(tz);
    if (fromTz) return fromTz;
  } catch {
    /* fall through to locale */
  }
  return detectCountryFromLocale(navigator.language);
}

/**
 * Coerce an untrusted country value to a supported ISO2 code, falling back to
 * `DEFAULT_COUNTRY_ISO2`. Use this at every boundary that accepts a country from
 * a request body, a credential, or a locale string.
 */
export function resolveCountryIso2(value: unknown): string {
  if (typeof value === 'string' && isSupportedCountry(value)) {
    return value.trim().toUpperCase();
  }
  return DEFAULT_COUNTRY_ISO2;
}

/**
 * Look up a country by E.164 dial code without a leading `+`. Because dial codes
 * are not prefix-free (`1` = US/CA, `233` = GH), callers splitting a digit string
 * must test the longest code first — `getSupportedDialCodes()` returns them in
 * that order.
 */
export function getCountryByDialCode(
  dial: string | null | undefined,
  table: readonly SupportedCountry[] = SUPPORTED_COUNTRIES
): SupportedCountry | undefined {
  if (!dial) return undefined;
  const code = dial.replace(/\D/g, '');
  return table.find((country) => country.dial === code);
}

/** The default country record (Ghana). */
export function getDefaultCountry(
  table: readonly SupportedCountry[] = SUPPORTED_COUNTRIES
): SupportedCountry | undefined {
  return getCountryByIso2(DEFAULT_COUNTRY_ISO2, table);
}

/**
 * Unique dial codes, longest first, so a longest-prefix scan finds `233` before
 * `1`/`27`. Deduped because US and CA share `1`.
 */
export function getSupportedDialCodes(
  table: readonly SupportedCountry[] = SUPPORTED_COUNTRIES
): string[] {
  return [...new Set(table.map((country) => country.dial))].sort(
    (a, b) => b.length - a.length
  );
}

/** Countries with mobile-money collection enabled. */
export function getMomoCountries(): SupportedCountry[] {
  return SUPPORTED_COUNTRIES.filter((country) => country.momo);
}
